import React from 'react';
import {
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Button,
  ListBox,
  type ListBoxItemProps,
  SelectValue,
  type ValidationResult,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { Description, FieldError, Label } from './field';
import { DropdownItem, DropdownSection, type DropdownSectionProps } from './list-box';
import { Popover } from './popover';
import { composeTailwindRenderProps, focusRing } from './component-utils';
import { LabChevronDownIcon } from 'components/camperlab/icons/chevron-down';

const styles = tv({
  extend: focusRing,
  base: 'flex h-8 w-full min-w-[150px] cursor-default items-center gap-4 border border-dark px-1.5 text-start text-sm transition lg:h-6 lg:text-xs',
  variants: {
    isDisabled: {
      false: 'text-copy group-invalid:border-error forced-colors:group-invalid:border-[Mark]',
      true: 'border-disabled text-disabled forced-colors:border-[GrayText] forced-colors:text-[GrayText]',
    },
  },
});

export interface SelectProps<T extends object> extends Omit<AriaSelectProps<T>, 'children'> {
  label?: string;
  hiddenLabel?: boolean;
  required?: boolean;
  tvStyles?: any;
  selectClassName?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  icon?: React.ReactNode;
  children: React.ReactNode | ((item: T) => React.ReactNode);
}

export function Select<T extends object>({
  label,
  hiddenLabel,
  tvStyles,
  selectClassName,
  description,
  errorMessage,
  children,
  items,
  icon,
  required = false,
  ...props
}: SelectProps<T>) {
  return (
    <AriaSelect {...props} isRequired={required} className={composeTailwindRenderProps(props.className, 'group flex flex-col gap-1')}>
      {label && <Label className={hiddenLabel ? 'sr-only' : ''}>{label}</Label>}
      <Button className={tvStyles || styles}>
        <SelectValue className={selectClassName || 'flex-1 text-sm lg:text-xs'} />
        {icon || (
          <LabChevronDownIcon
            aria-hidden
            className="size-3.5 text-copy group-disabled:text-disabled forced-colors:text-[ButtonText] forced-colors:group-disabled:text-[GrayText]"
          />
        )}
      </Button>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <Popover crossOffset={0} className="min-w-[--trigger-width]">
        <ListBox items={items} className="max-h-[inherit] overflow-auto outline-none">
          {children}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

export function SelectItem(props: ListBoxItemProps) {
  return <DropdownItem {...props} />;
}

export function SelectSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection {...props} />;
}
